import React, { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import Breadcrumb from "components/Breadcrumb";
import Button from "components/Button";
import { Board, TitleArea, ContentArea, FormRow, FooterArea } from "components/LayoutParts";
import UserModal, { UserModalConfig } from "components/UserModal";
import font from "constants/font";
import { useFindLatestAiSoftwareVersions } from "pages/AimoParking/AiSoftware/api";
import AimoParkingSelect from "pages/AimoParking/component/AimoParkingSelect";
import { useFindEdgeDevice, useUpdateEdgeDeviceAiVersion } from "pages/AimoParking/EdgeDevice/api";
import {
  CONFIRM_MODAL_CONFIG,
  FAILED_MODAL_CONFIG,
  INITIAL_MODAL_CONFIG,
  SUCCEED_MODAL_CONFIG,
} from "pages/AimoParking/modalTemplate";

const Label = styled.div`
  min-width: 200px;
  margin: 0 5px;
  padding-right: 18px;
`;

const Value = styled.div`
  font-size: ${font.size16};
`;

const Wrap = styled.div`
  margin-top: 10px;
  flex-direction: column;
  justify-content: space-between;
  display: flex;
  flex: 1;
`;

const CancelWrap = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;

const SaveWrap = styled(CancelWrap)`
  align-items: center;
  justify-content: flex-end;
`;

const Annotation = styled.span`
  font-size: ${font.size12};
  margin-right: 16px;
`;

type ChoiceType = { key?: number | string; label: string; value: string; disabled?: boolean };

const EdgeDeviceUpdateAiVersion = () => {
  const { id: edgeDeviceId } = useParams();
  const navigate = useNavigate();
  const [selectedChoice, setSelectedChoice] = useState<ChoiceType | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState<UserModalConfig>(INITIAL_MODAL_CONFIG);

  const { data: edgeDevice } = useFindEdgeDevice({ edgeDeviceId: edgeDeviceId ?? "" });
  const { data: latestAiSoftwares } = useFindLatestAiSoftwareVersions();
  const { updateAiVersion } = useUpdateEdgeDeviceAiVersion();

  const list = Array.isArray(latestAiSoftwares)
    ? latestAiSoftwares
    : latestAiSoftwares != null
      ? [latestAiSoftwares as AimoParkingAiSoftwareResponse]
      : [];
  const choices: ChoiceType[] = list.map((item) => ({
    label: item.version,
    value: item.id,
  }));

  const canSubmit = selectedChoice != null;

  const onSubmit = useCallback(async () => {
    if (edgeDeviceId == null || selectedChoice == null) return;
    const response = await updateAiVersion({
      edgeDeviceId,
      aiSoftwareId: selectedChoice.value,
    }).catch((e) => {
      setModalConfig(FAILED_MODAL_CONFIG({ onClose: () => setIsVisible(false) }));
      setIsVisible(true);
      throw e;
    });

    if (response.status === 200) {
      setModalConfig(
        SUCCEED_MODAL_CONFIG({
          message: "送信しました",
          onClose: () => navigate(`/aimo_parking/edge_device/${edgeDeviceId}`),
        })
      );
      setIsVisible(true);
    }
  }, [edgeDeviceId, selectedChoice, updateAiVersion, navigate]);

  const handleOpenSubmitModal = useCallback(() => {
    setModalConfig(
      CONFIRM_MODAL_CONFIG({
        negative: () => setIsVisible(false),
        positive: onSubmit,
        message: "AIバージョンを更新しますか？",
      })
    );
    setIsVisible(true);
  }, [onSubmit]);

  return (
    <Board>
      <TitleArea>
        <Breadcrumb
          currentPageName="AIバージョンを変更"
          breadcrumbItems={[
            {
              pageName: "エッジデバイス",
              onClick: () => navigate("/aimo_parking/edge_device"),
            },
            {
              pageName: edgeDevice?.serialNumber ?? "読み込み中",
              onClick: () => navigate(`/aimo_parking/edge_device/${edgeDeviceId}`),
            },
          ]}
        />
      </TitleArea>
      {edgeDevice && (
        <Wrap>
          <ContentArea>
            <FormRow>
              <Label>シリアルナンバー</Label>
              <Value>{edgeDevice.serialNumber}</Value>
            </FormRow>
            <FormRow>
              <Label>現在のAIバージョン</Label>
              <Value>{edgeDevice.currentAiSoftware?.version ?? "-"}</Value>
            </FormRow>
            <FormRow>
              <Label>変更後のAIバージョン</Label>
              <AimoParkingSelect
                choices={choices}
                value={selectedChoice}
                onChange={setSelectedChoice}
                width="350px"
                placeholder="バージョンを選択"
              />
            </FormRow>
          </ContentArea>
          <FooterArea>
            <CancelWrap>
              <Button
                type="secondary"
                onClick={() => navigate(`/aimo_parking/edge_device/${edgeDeviceId}`)}
                label="キャンセル"
                width="160px"
              />
            </CancelWrap>
            <SaveWrap>
              <Annotation>更新するまで入力内容は反映されません</Annotation>
              <Button
                type="primary"
                onClick={handleOpenSubmitModal}
                label="AIバージョンを更新"
                disabled={!canSubmit}
                width="191px"
              />
            </SaveWrap>
          </FooterArea>
        </Wrap>
      )}
      <UserModal isVisible={isVisible} config={modalConfig} />
    </Board>
  );
};

export default EdgeDeviceUpdateAiVersion;
